import { shortcuts } from "../../../../../app/javascript/keyboard/commands"
import { Controller } from "@hotwired/stimulus"

const pendingSends = new Set()
const draftFields = ["transport", "content", "email", "phone", "subject"]

export default class extends Controller {
  static targets = ["transport", "content", "email", "phone", "subject", "emailFields", "phoneFields", "draftStatus", "send", "status", "error"]
  static values = { channelId: Number, userId: Number, organizationId: Number }

  connect() {
    this.unregisterShortcut = shortcuts.register({
      id: "message.send", label: "Send message", element: this.sendTarget,
      enabled: () => !this.sending && Boolean(this.contentTarget.value.trim()),
      action: () => this.sendTarget.click()
    })
    this.storageKey = `message-draft:${this.userIdValue}:${this.organizationIdValue}:${this.channelIdValue}`
    this.restoreDraft()
    if (!this.transportTarget.value) this.transportTarget.value = "chat"
    this.sending = pendingSends.has(this.storageKey)
    this.completed = event => {
      if (event.detail.key !== this.storageKey) return
      this.sending = false
      if (event.detail.success) {
        if (JSON.stringify(this.draft()) === JSON.stringify(event.detail.draft)) this.contentTarget.value = ""
        this.statusTarget.textContent = event.detail.draft.transport === "chat" ? "Sent" : "Queued for delivery"
        this.errorTarget.classList.add("d-none")
      } else {
        this.errorTarget.textContent = event.detail.error
        this.errorTarget.classList.remove("d-none")
        this.statusTarget.textContent = "Send not confirmed — draft kept"
      }
      this.sendTarget.textContent = "Send"
      this.saveDraft()
    }
    document.addEventListener("communication:send-complete", this.completed)
    if (this.sending) {
      this.statusTarget.textContent = "Sending message…"
      this.sendTarget.textContent = "Sending…"
    }
    this.update()
    this.saveDraft()
  }

  disconnect() {
    this.unregisterShortcut?.()
    document.removeEventListener("communication:send-complete", this.completed)
  }

  readDraft() {
    try { return JSON.parse(sessionStorage.getItem(this.storageKey)) } catch (_error) { return null }
  }

  restoreDraft() {
    const draft = this.readDraft()
    if (!draft || typeof draft !== "object") return
    for (const field of draftFields) {
      if (typeof draft[field] === "string") this[`${field}Target`].value = draft[field]
    }
  }

  draft() {
    return Object.fromEntries(draftFields.map(field => [field, this[`${field}Target`].value]))
  }

  update() {
    const email = this.transportTarget.value === "email"
    const sms = this.transportTarget.value === "sms"
    this.emailFieldsTarget.classList.toggle("d-none", !email)
    this.phoneFieldsTarget.classList.toggle("d-none", !sms)
    this.emailTarget.disabled = !email
    this.subjectTarget.disabled = !email
    this.phoneTarget.disabled = !sms
    this.emailTarget.required = email
    this.phoneTarget.required = sms
  }

  saveDraft() {
    const draft = this.draft()
    this.sendTarget.disabled = this.sending || !draft.content.trim()
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(draft))
      this.draftStatusTarget.textContent = draft.content || draft.subject ? "Draft saved" : ""
    } catch (_error) {
      this.draftStatusTarget.textContent = "Draft storage unavailable"
    }
  }

  async send(event) {
    event.preventDefault()
    if (pendingSends.has(this.storageKey) || !this.contentTarget.value.trim() || !this.element.reportValidity()) return
    const body = new FormData(this.element)
    const draft = this.draft()
    const panel = this.element.closest("#messagesOffcanvas")
    pendingSends.add(this.storageKey)
    this.sending = true
    this.sendTarget.disabled = true
    this.sendTarget.textContent = "Sending…"
    this.statusTarget.textContent = "Sending message…"
    this.errorTarget.classList.add("d-none")
    let result
    try {
      const response = await fetch(this.element.action, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || response.redirected || !data.html) {
        const error = typeof data.error === "string" ? data.error : data.error?.message
        throw new Error(error || "The message could not be sent. Please try again.")
      }
      // A different composer may now be mounted. Clear only the exact draft sent.
      if (JSON.stringify(this.readDraft()) === JSON.stringify(draft)) {
        try { sessionStorage.setItem(this.storageKey, JSON.stringify({ ...draft, content: "" })) } catch (_error) { /* Optional storage. */ }
      }
      panel?.dispatchEvent(new CustomEvent("communication:sent", { bubbles: true, detail: { channelId: this.channelIdValue, message: data } }))
      result = { success: true }
    } catch (error) {
      result = {
        success: false,
        error: error instanceof TypeError ? "Connection lost. Your draft is kept. Check the conversation before sending again." : error.message
      }
    } finally {
      pendingSends.delete(this.storageKey)
      document.dispatchEvent(new CustomEvent("communication:send-complete", { detail: { ...result, key: this.storageKey, draft } }))
    }
  }
}
